import { useEffect } from 'react';
import message from 'antd/es/message';
import { useQuery, useMutation, InfiniteData } from 'react-query';
import produce from 'immer';

import { EditorSubmitContentData } from '@/components/RichTextEditor/types';
import { streamActionToast } from '@/components/SystemErrorToast';
import { useQueryParams, useUserPermission } from '@/hooks';
import { request, queryClient, useCurrentUserId, toastError } from '@/utils';
import { invalidateChannelStreamList } from '@/views/Channel/hooks/query';

import { FeedRefetchInterval, STREAM_TRIAL_LIMIT_CODE } from '../const';
import { transArticle } from '../utils';
import { FeedArticle, FeedArticleDetail, FeedItem, ArticleCommentEligibility } from '../types';
import { LIST_URL } from './list';
import { useIsLastFeedUnRead, invalidLastFeedRead } from './count';
import { setQueryAllFeedDetailByArticleId } from './setQuery';

const transFeedArticle = (data: FeedArticleDetail) => {
  return produce(data, draft => {
    transArticle(draft.article);
  });
};

export const useFeedArticle = (id: string, initialData?: FeedArticleDetail) => {
  const currentAddress = useCurrentUserId();
  const url = '/article';
  const [privateToken] = useQueryParams<string>('private_token');

  useEffect(() => {
    // 组件 currentAddress 更新时，移除之前的 cache
    return () => {
      console.log('unmount post detail', id);
      removeArticleDetail(Number(id), currentAddress);
    };
    // eslint-disable-next-line
  }, [currentAddress]);

  return useQuery(
    [url, currentAddress, Number(id)],
    () => {
      return request.get<FeedArticleDetail>(url, {
        id,
        private_token: privateToken,
      });
    },
    {
      staleTime: FeedRefetchInterval,
      cacheTime: FeedRefetchInterval,
      enabled: !!id,
      select: transFeedArticle,
      refetchOnReconnect: true,
      refetchOnWindowFocus: true,
      ...(initialData
        ? {
            initialData,
            initialDataUpdatedAt: initialData.article.updateAt,
          }
        : {}),
      onError(err: any) {
        if (err !== 'article not found') {
          toastError('failed to fetch, please try later');
        }
      },
    },
  );
};

export const invalidArticleDetail = (id: number, currentAddress?: string) => {
  return queryClient.invalidateQueries(['/article', currentAddress, id]);
};

export const removeArticleDetail = (id: number, currentAddress?: string) => {
  return queryClient.removeQueries(['/article', currentAddress, id]);
};

export const setArticleDetailQuery = (
  id: number,
  currentAddress?: string,
  updater?: (x?: FeedArticleDetail) => FeedArticleDetail | undefined,
) => {
  return queryClient.setQueriesData<FeedArticleDetail | undefined>(
    ['/article', currentAddress, Number(id)],
    updater,
  );
};

export const setArticleQuoteDetailQuery = (
  id: number,
  currentAddress?: string,
  updater?: (x?: FeedArticleDetail) => FeedArticleDetail | undefined,
) => {
  return queryClient.setQueriesData<FeedArticleDetail | undefined>(
    ['/article', currentAddress],
    pre => {
      if (pre?.article?.quote_article?.id === id) {
        return produce(pre, draft => {
          draft.article.quote_article = updater?.({
            article: pre?.article?.quote_article!,
          })!.article;
        });
      }

      return pre;
    },
  );
};

// 发布 feed
export const useFeedCreate = (hideToast?: boolean) => {
  const currentAddress = useCurrentUserId();

  return useMutation(
    async (params: EditorSubmitContentData) => {
      return await request.post<{ article: FeedArticle }>('/article/add', params);
    },
    {
      onSuccess(res, req) {
        if (!hideToast) {
          if (!res?.article) {
            toastError('post failed');

            return;
          } else {
            message.success('posted');
          }
        }

        if (currentAddress && res.article?.channel?.id) {
          // channel 的 stream 列表要重刷下
          invalidateChannelStreamList(currentAddress, res.article.channel.id);
        }

        queryClient.setQueriesData<
          InfiniteData<Record<(typeof LIST_URL)['feeds']['resKey'], FeedItem[]>> | undefined
        >([LIST_URL.feeds.url, currentAddress], pre => {
          if (!pre) {
            return pre;
          }

          const feed = {
            article: {
              ...res.article,
              read_count: 0,
              repost_count: 0,
              trust_count: 0,
              comment_count: 0,
              distrust_count: 0,
            },
            feed_type: 'original' as const,
            // 加点时间，免得 new_count 请求到这一条
            create_at: res.article.create_at + 1,
            repost_list: [],
            id: res.article.id,
          };

          return produce(pre, draft => {
            if (draft.pages.length) {
              draft.pages[0].feeds.unshift(feed);
            } else {
              draft.pages.push({
                feeds: [feed],
              });
            }
          });
        });

        if (res.article.type === 'quote' && req.quote_article_id) {
          const handle = (pre?: FeedArticleDetail) => {
            if (pre) {
              pre.article.quote_count = (pre.article.quote_count || 0) + 1;
            }
          };
          setQueryAllFeedDetailByArticleId({
            currentAddress: currentAddress!,
            articleId: req.quote_article_id,
            recipe: handle,
          });
        }
      },
      onError(err: any) {
        if (hideToast) {
          return;
        }
        toastError(err || 'Failed');
      },
    },
  );
};

// 编辑 post
export const useFeedEdit = (id: number) => {
  return useMutation(
    async (params: EditorSubmitContentData) => {
      return await request.post<{ is_success: boolean }>('/article/edit', { id, ...params });
    },
    {
      onSuccess(res) {
        if (res?.is_success) {
          message.success('success');
        } else {
          toastError('edit failed');
        }
      },
      onError(err: any) {
        toastError(err || 'Failed');
      },
    },
  );
};

// 标记已读
export const useArticleRead = () => {
  const { data } = useIsLastFeedUnRead();

  return useMutation(
    (params: {
      id: number;
      contributor_id?: string;
      source?: 'timeline' | 'detail';
      ref_code?: number;
    }) => {
      return request
        .post<{ is_success: boolean }>('/article/read', params, { hideMessage: true })
        .catch(() => undefined);
    },
    {
      onSuccess(res, { id }) {
        if (data && !data.is_read && data.article_id === id) {
          invalidLastFeedRead();
        }
      },
    },
  );
};

const setRepostData = (id: number, reposted: boolean, currentAddress?: string) => {
  const recipe = (pre?: FeedArticleDetail) => {
    if (pre?.article) {
      pre.article.is_reposted = reposted;
      pre.article.repost_count = reposted
        ? (pre.article.repost_count || 0) + 1
        : pre.article.repost_count > 1
          ? pre.article.repost_count - 1
          : 0;
    }
  };

  setQueryAllFeedDetailByArticleId({
    currentAddress: currentAddress!,
    articleId: id,
    recipe,
  });
};

export const useArticleRepost = (id: number) => {
  const currentAddress = useCurrentUserId();

  return useMutation(
    () => {
      setRepostData(id, true, currentAddress);
      return request.post<{ is_success: boolean }>('/article/repost', {
        id,
      });
    },
    {
      onSuccess(res) {
        if (!res?.is_success) {
          toastError('Repost failed');

          setRepostData(id, false, currentAddress);
        }
      },
      onError(err: any) {
        setRepostData(id, false, currentAddress);
        if (err?.error_code === STREAM_TRIAL_LIMIT_CODE) {
          streamActionToast('repost');
        } else {
          toastError(err || 'Failed');
        }
      },
    },
  );
};

const changeTrustState = (pre?: FeedArticleDetail, is_trust?: boolean | null) => {
  if (pre?.article) {
    const [isTrust, isDistrust] = [pre.article.is_trust, pre.article.is_trust === false];

    if (is_trust) {
      pre.article.trust_count = (pre.article.trust_count || 0) + 1;
      if (isDistrust) {
        pre.article.distrust_count =
          pre.article.distrust_count > 1 ? pre.article.distrust_count - 1 : 0;
      }
    } else if (is_trust === false) {
      pre.article.distrust_count = (pre.article.distrust_count || 0) + 1;

      if (isTrust) {
        pre.article.trust_count = pre.article.trust_count > 1 ? pre.article.trust_count - 1 : 0;
      }
    } else {
      // null，取消 trust 或者 distrust
      if (isTrust) {
        pre.article.trust_count = pre.article.trust_count > 1 ? pre.article.trust_count - 1 : 0;
      } else if (isDistrust) {
        pre.article.distrust_count =
          pre.article.distrust_count > 1 ? pre.article.distrust_count - 1 : 0;
      }
    }

    pre.article.is_trust = is_trust!;

    return pre;
  }
};

const changeTrustData = (id: number, is_trust: boolean | null, currentAddress?: string) => {
  setQueryAllFeedDetailByArticleId({
    currentAddress: currentAddress!,
    articleId: id,
    recipe: (pre?: FeedArticleDetail) => {
      changeTrustState(pre, is_trust);
    },
  });
};

export const useArticleTrust = (id: number, oldTrust: boolean | null) => {
  const currentAddress = useCurrentUserId();
  let isTrustAction: boolean;

  return useMutation(
    (is_trust: boolean | null) => {
      changeTrustData(id, is_trust, currentAddress);
      isTrustAction = !!is_trust;
      return request.post<{ is_success: boolean }>('/article/trust', {
        id,
        is_trust,
      });
    },
    {
      onSuccess(res, is_trust) {
        if (!res?.is_success) {
          toastError('Failed');
          changeTrustData(id, oldTrust, currentAddress);
        }
      },
      onError(err: any) {
        if (err?.error_code === STREAM_TRIAL_LIMIT_CODE) {
          streamActionToast(!!isTrustAction ? 'trust' : 'distrust');
        } else {
          toastError(err || 'Failed');
        }
        changeTrustData(id, oldTrust, currentAddress);
      },
    },
  );
};

// 撤回发布
export const useArticleWithdraw = (id: number) => {
  const currentAddress = useCurrentUserId();

  return useMutation(
    () => {
      return request.post<{ is_success: boolean }>('/article/withdraw', {
        id,
      });
    },
    {
      onSuccess(res) {
        if (!res?.is_success) {
          toastError('Failed');

          return;
        } else {
          message.success('Hidden');
        }

        setQueryAllFeedDetailByArticleId({
          currentAddress: currentAddress!,
          articleId: id,
          recipe: (pre?: FeedArticleDetail) => {
            if (pre) {
              pre.article.is_withdrawn = true;
            }
          },
          includeQuote: true,
        });
      },
      onError(err: any) {
        toastError(err || 'Failed');
      },
    },
  );
};

// 更新文章操作权限
export const useArticleEditCommentEligibility = (id: number) => {
  const currentAddress = useCurrentUserId();
  const { mutateAsync: requestCommentPermission } = useUserPermission('feed_comment');

  return useMutation(
    (permissions: ArticleCommentEligibility) => {
      return request.post<{ is_success: boolean }>('/article/update_permissions', {
        id,
        permissions,
      });
    },
    {
      async onSuccess(res, permissions) {
        if (!res?.is_success) {
          toastError('Failed');

          return;
        } else {
          message.success('Updated');
        }
        const commentPermission = await requestCommentPermission({ article_id: id });

        const handle = (pre?: FeedArticleDetail) => {
          if (pre) {
            pre.article.permissions = permissions;
            pre.article.has_permission = commentPermission?.has_permission;
          }
        };
        setQueryAllFeedDetailByArticleId({
          currentAddress: currentAddress!,
          articleId: id,
          recipe: handle,
        });
      },
      onError(err: any) {
        toastError(err || 'Failed');
      },
    },
  );
};

export const useArticleRepostDelete = (id: number) => {
  const currentAddress = useCurrentUserId();

  return useMutation(
    () => {
      setRepostData(id, false, currentAddress);
      return request.post<{ is_success: boolean }>('/article/delete_repost', {
        id,
      });
    },
    {
      onSuccess(res) {
        if (!res?.is_success) {
          toastError('Failed');
          setRepostData(id, true, currentAddress);
        }
      },
      onError(err: any) {
        toastError(err || 'Failed');
        setRepostData(id, true, currentAddress);
      },
    },
  );
};
